import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save, 
  ArrowLeft, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Tag, 
  AlertCircle,
  Package,
  ChevronDown
} from 'lucide-react';
import { Lead, Make, Model, ServiceType } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { LEAD_STATUSES } from '../constants/leadStatuses';

const CreateLeadPage: React.FC = () => {
  const navigate = useNavigate();

  // Form State
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [languagePreferred, setLanguagePreferred] = useState('');
  const [leadSource, setLeadSource] = useState('Website');
  const [leadStatus, setLeadStatus] = useState<string>('New');
  const [priority, setPriority] = useState('Medium');
  const [notes, setNotes] = useState('');

  // Cascading dropdowns
  const [makes, setMakes] = useState<Make[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedMake, setSelectedMake] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<number | null>(null);

  // Search state for autocomplete
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [filteredMakes, setFilteredMakes] = useState<Make[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingMakes, setLoadingMakes] = useState(true);

  useEffect(() => {
    loadMakes();
  }, []);

  // Filter makes based on search
  useEffect(() => {
    if (makeSearch) {
      const filtered = makes.filter(make => 
        make.name.toLowerCase().includes(makeSearch.toLowerCase())
      );
      setFilteredMakes(filtered);
    } else {
      setFilteredMakes(makes);
    }
  }, [makes, makeSearch]);

  // Filter models based on search
  useEffect(() => {
    if (modelSearch) {
      const filtered = models.filter(model => 
        model.name.toLowerCase().includes(modelSearch.toLowerCase())
      );
      setFilteredModels(filtered);
    } else {
      setFilteredModels(models);
    }
  }, [models, modelSearch]);

  const loadMakes = async () => {
    try {
      setLoadingMakes(true);
      const response = await apiService.getMakes();
      if (response.isSuccess && response.entity) {
        setMakes(response.entity);
        setFilteredMakes(response.entity);
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      toast.error('Error loading makes');
    } finally {
      setLoadingMakes(false);
    }
  };

  const loadModels = async (makeId: number) => {
    try {
      const response = await apiService.getModels(makeId);
      if (response.isSuccess && response.entity) {
        setModels(response.entity);
        setFilteredModels(response.entity);
      }
    } catch (error) {
      console.error('Error loading models:', error);
      toast.error('Error loading models');
    }
  };

  const loadServiceTypes = async (modelId: number) => {
    try {
      const response = await apiService.getServiceTypes(modelId);
      if (response.isSuccess && response.entity) {
        setServiceTypes(response.entity);
      }
    } catch (error) {
      console.error('Error loading service types:', error);
      toast.error('Error loading service types');
    }
  };

  const handleMakeSelect = (make: Make) => {
    setSelectedMake(make.id);
    setMakeSearch(make.name);
    setShowMakeDropdown(false);
    // Clear model selection
    setSelectedModel(null);
    setModelSearch('');
    setServiceTypes([]);
    // Small delay to ensure state is updated before loading models
    setTimeout(() => {
      loadModels(make.id);
    }, 0);
  };

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model);
    setModelSearch(model.name);
    setShowModelDropdown(false);
    // Small delay to ensure state is updated before loading services
    setTimeout(() => {
      loadServiceTypes(model.id);
    }, 0);
  };

  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    if (!customerMobile.trim() || customerMobile.length !== 10) {
      toast.error('Valid 10-digit mobile number is required');
      return false;
    }
    if (!leadSource) {
      toast.error('Lead source is required');
      return false;
    }
    if (!priority) {
      toast.error('Priority is required');
      return false;
    }
    if (!leadStatus || !leadStatus.trim()) {
      toast.error('Lead status is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      const leadData = {
        name: customerName,
        mobile: customerMobile,
        email: customerEmail || '',
        address: customerAddress || '',
        languagePreferred: languagePreferred || '',
        source: leadSource,
        status: leadStatus,
        priority,
        notes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        assignedTo: 1,
        assignedToName: 'Sales Team',
        expectedValue: 0,
        actualValue: 0,
        deviceType: selectedMake ? makes.find(m => m.id === selectedMake)?.name || '' : '',
        deviceModel: selectedModel ? selectedModel.name || '' : '',
        serviceRequired: selectedServiceType ? serviceTypes.find(s => s.id === selectedServiceType)?.serviceName || '' : '',
        createdBy: 1
      } as Omit<Lead, 'id'>;

      const response = await apiService.createLead(leadData as Lead);
      
      if (response.isSuccess) {
        toast.success('Lead created successfully');
        navigate('/leads');
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to create lead');
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Error creating lead');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/leads');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Create New Lead</h1>
              <p className="text-sm text-gray-500">Add a new customer inquiry or potential sale</p>
            </div>
          </div>
        </div>
        
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Create Lead
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Customer Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit mobile number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Enter customer address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Language Preferred</label>
                <select
                  value={languagePreferred}
                  onChange={(e) => setLanguagePreferred(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Language</option>
                  <option value="Hindi">Hindi</option>
                  <option value="English">English</option>
                  <option value="Marathi">Marathi</option>
                </select>
              </div>
            </div>
          </div>

          {/* Device Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Package className="h-4 w-4 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Device Information</h3>
              <span className="text-sm text-gray-500">(Optional)</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                <div className="relative">
                  <input
                    type="text"
                    value={makeSearch}
                    onChange={(e) => {
                      setMakeSearch(e.target.value);
                      setShowMakeDropdown(true);
                    }}
                    onFocus={() => setShowMakeDropdown(true)}
                    onClick={() => {
                      setShowMakeDropdown(true);
                      // Show all makes when clicked, not just filtered
                      if (!makeSearch) {
                        setFilteredMakes(makes);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowMakeDropdown(false), 300)}
                    placeholder="Search and select make..."
                    disabled={loadingMakes}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                  {showMakeDropdown && filteredMakes.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredMakes.map((make) => (
                        <button
                          key={make.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleMakeSelect(make);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                        >
                          <div className="flex items-center space-x-3">
                            {make.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(make.imagePath)}
                                alt={make.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{make.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <div className="relative">
                  <input
                    type="text"
                    value={modelSearch}
                    onChange={(e) => {
                      setModelSearch(e.target.value);
                      setShowModelDropdown(true);
                    }}
                    onFocus={() => setShowModelDropdown(true)}
                    onClick={() => {
                      if (selectedMake) {
                        setShowModelDropdown(true);
                        // Show all models when clicked, not just filtered
                        if (!modelSearch) {
                          setFilteredModels(models);
                        }
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowModelDropdown(false), 300)}
                    placeholder="Search and select model..."
                    disabled={!selectedMake}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className={`h-4 w-4 ${selectedMake ? 'text-gray-400' : 'text-gray-300'}`} />
                  </div>
                  {showModelDropdown && filteredModels.length > 0 && selectedMake && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredModels.map((model) => (
                        <button
                          key={model.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleModelSelect(model);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                        >
                          <div className="flex items-center space-x-3">
                            {model.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(model.imagePath)}
                                alt={model.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{model.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
                <select
                  value={selectedServiceType || ''}
                  onChange={(e) => setSelectedServiceType(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!selectedModel}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">Select Service</option>
                  {serviceTypes.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.serviceName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Additional Notes</h3>
            </div>
            
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Enter any additional notes, requirements, or customer comments..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Right Column - Lead Details */}
        <div className="space-y-6">
          {/* Lead Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <Tag className="h-4 w-4 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Lead Details</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Source <span className="text-red-500">*</span>
                </label>
                <select
                  value={leadSource}
                  onChange={(e) => setLeadSource(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="Website">Website</option>
                  <option value="Phone">Phone</option>
                  <option value="Email">Email</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Referral">Referral</option>
                  <option value="Walk-in">Walk-in</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={leadStatus}
                  onChange={(e) => setLeadStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Lead Status</option>
                  {LEAD_STATUSES.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority <span className="text-red-500">*</span>
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            
            <div className="space-y-3">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Create Lead
              </button>
              
              <button
                onClick={handleCancel}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLeadPage; 